// Upload helpers for the multipart endpoints (docs/api-contract.md §4.9).
// All take the client from useApiClient(); returns are the created resource JSON.

import { API_BASE_URL, ApiError } from './client'

export function uploadProfileImage(client, file, imageType = 'profile') {
  const form = new FormData()
  form.append('image', file)
  form.append('image_type', imageType)
  return client.request('/api/v1/uploads/profile-image', { method: 'POST', body: form })
}

export function uploadProductImage(client, file, productId) {
  const form = new FormData()
  form.append('image', file)
  form.append('product_id', String(productId))
  return client.request('/api/v1/uploads/product-image', { method: 'POST', body: form })
}

export function uploadSellerMedia(client, file, { title, caption, publish_at } = {}) {
  const form = new FormData()
  form.append('file', file)
  if (title) form.append('title', title)
  if (caption) form.append('caption', caption)
  // Optional scheduling (contract §5): future ISO timestamp; omitted → publish now
  if (publish_at) form.append('publish_at', publish_at)
  return client.request('/api/v1/uploads/seller-media', { method: 'POST', body: form })
}

export function uploadSellerBanner(client, file) {
  const form = new FormData()
  form.append('image', file)
  return client.request('/api/v1/uploads/seller-banner', { method: 'POST', body: form })
}

function buildIdVerificationForm({ selfieWithId, frontOfId, backOfId, barcodeData } = {}) {
  const form = new FormData()
  if (selfieWithId) form.append('selfie_with_id', selfieWithId)
  if (frontOfId) form.append('front_of_id', frontOfId)
  if (backOfId) form.append('back_of_id', backOfId)
  if (barcodeData) form.append('barcode_data', typeof barcodeData === 'string' ? barcodeData : JSON.stringify(barcodeData))
  return form
}

export function uploadIdVerification(client, files = {}) {
  return client.request('/api/v1/uploads/id-verification', { method: 'POST', body: buildIdVerificationForm(files) })
}

// Buyer-identity twin of the seller route (contract §4.23): same form fields,
// resubmission replaces files and resets status to pending. Rate limit 3/5min.
export function uploadBuyerIdVerification(client, files = {}) {
  return client.request('/api/v1/uploads/buyer-id-verification', { method: 'POST', body: buildIdVerificationForm(files) })
}

// Auth-gated binary routes (verification files) can't be used in <img src> directly —
// fetch with the bearer token and return an object URL. Caller revokes it on cleanup.
export async function fetchProtectedBlobUrl(client, path) {
  let token = null
  try {
    token = await client.getToken()
  } catch {
    // signed out — request proceeds unauthenticated and will 401 below
  }
  const headers = token ? { Authorization: `Bearer ${token}` } : {}
  let res
  try {
    res = await fetch(`${API_BASE_URL}${path}`, { headers })
  } catch (err) {
    throw new ApiError({ status: 0, code: 'network', title: 'Network error', detail: err?.message })
  }
  if (!res.ok) {
    throw new ApiError({ status: res.status, code: res.status === 404 ? 'not_found' : 'auth', title: res.statusText, detail: null })
  }
  return URL.createObjectURL(await res.blob())
}
